import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  ElementRef,
  EventEmitter,
  HostListener,
  inject,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
} from "@angular/core";
import { CommonModule } from "@angular/common";
import { NgIconComponent } from "@ng-icons/core";
import { isNgIconName } from "../utils/icon";
import { lockBodyScroll, unlockBodyScroll } from "../styles/body-scroll-lock";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import {
  EvaCommandPaletteRecentsService,
  RecentItem,
  RecentItemType,
  RECENT_ITEM_ICONS,
  RECENT_ITEM_LABELS,
} from "./eva-command-palette-recents.service";

// Re-export pour utilisation externe
export { RecentItem, RecentItemType, RECENT_ITEM_ICONS, RECENT_ITEM_LABELS };

// =============================================================================
// TYPES
// =============================================================================

/**
 * Preset de configuration de la command palette.
 * - standard: Recherche + récents + actions génériques (Navigation, Actions)
 * - project: Quick access chantiers + phases + tâches (Mes chantiers, Phases, Tâches)
 * - admin: Actions admin + rapports + exports (Administration, Rapports, Exports)
 */
export type CommandPalettePreset = "standard" | "project" | "admin";

// =============================================================================
// INTERFACES
// =============================================================================

/**
 * Item dans une section de la command palette
 */
export interface CommandItem {
  /** Identifiant unique */
  id: string;
  /** Label affiché */
  label: string;
  /** Icône PrimeIcon (ex: "pi pi-home") */
  icon?: string;
  /** Raccourci clavier affiché (ex: "Cmd+N") */
  shortcut?: string;
  /** Action à exécuter au clic/Enter */
  action: () => void;
  /** Description optionnelle */
  description?: string;
  /** Désactiver l'item */
  disabled?: boolean;
  /** Données supplémentaires */
  data?: any;
}

/**
 * Section regroupant des items
 */
export interface CommandSection {
  /** Identifiant unique */
  id: string;
  /** Label de la section */
  label: string;
  /** Items de la section */
  items: CommandItem[];
  /** Icône de la section */
  icon?: string;
}

// =============================================================================
// PRESET CONFIGURATIONS
// =============================================================================

// Action par défaut pour les presets de démonstration.
const NOOP_COMMAND_ACTION = (): void => undefined;

/**
 * Configuration des sections par défaut pour le preset "standard"
 * Sections: Récents, Navigation, Actions
 */
export const PRESET_STANDARD_SECTIONS: CommandSection[] = [
  {
    id: "navigation",
    label: "Navigation",
    icon: "evaCompass",
    items: [
      {
        id: "nav-dashboard",
        label: "Tableau de bord",
        icon: "heroHome",
        shortcut: "G D",
        action: NOOP_COMMAND_ACTION,
      },
      {
        id: "nav-clients",
        label: "Clients",
        icon: "heroUsers",
        shortcut: "G C",
        action: NOOP_COMMAND_ACTION,
      },
      {
        id: "nav-offers",
        label: "Offre",
        icon: "heroPencilSquare",
        shortcut: "G O",
        action: NOOP_COMMAND_ACTION,
      },
      {
        id: "nav-invoices",
        label: "Factures",
        icon: "heroReceiptPercent",
        shortcut: "G F",
        action: NOOP_COMMAND_ACTION,
      },
      {
        id: "nav-planning",
        label: "Planning",
        icon: "heroCalendar",
        shortcut: "G P",
        action: NOOP_COMMAND_ACTION,
      },
    ],
  },
  {
    id: "actions",
    label: "Actions",
    icon: "heroBolt",
    items: [
      {
        id: "action-new-client",
        label: "Nouveau client",
        icon: "heroUserPlus",
        shortcut: "N C",
        action: NOOP_COMMAND_ACTION,
      },
      {
        id: "action-new-offer",
        label: "Nouvelle offre",
        icon: "heroDocumentPlus",
        shortcut: "N O",
        action: NOOP_COMMAND_ACTION,
      },
      {
        id: "action-new-invoice",
        label: "Nouvelle facture",
        icon: "heroPlusCircle",
        shortcut: "N F",
        action: NOOP_COMMAND_ACTION,
      },
    ],
  },
];

/**
 * Configuration des sections par défaut pour le preset "project"
 * Sections: Récents, Mes chantiers, Phases, Tâches
 */
export const PRESET_PROJECT_SECTIONS: CommandSection[] = [
  {
    id: "projects",
    label: "Mes chantiers",
    icon: "heroBuildingOffice",
    items: [
      {
        id: "project-all",
        label: "Tous les chantiers",
        icon: "heroListBullet",
        shortcut: "G P",
        action: NOOP_COMMAND_ACTION,
      },
      {
        id: "project-active",
        label: "Chantiers en cours",
        icon: "heroPlay",
        description: "Filtrer les chantiers actifs",
        action: NOOP_COMMAND_ACTION,
      },
      {
        id: "project-new",
        label: "Nouveau chantier",
        icon: "heroPlus",
        shortcut: "N P",
        action: NOOP_COMMAND_ACTION,
      },
    ],
  },
  {
    id: "phases",
    label: "Phases",
    icon: "evaSitemap",
    items: [
      {
        id: "phase-planning",
        label: "Planification",
        icon: "heroCalendar",
        description: "Chantiers en phase de planification",
        action: NOOP_COMMAND_ACTION,
      },
      {
        id: "phase-execution",
        label: "Exécution",
        icon: "heroCog6Tooth",
        description: "Chantiers en phase d'exécution",
        action: NOOP_COMMAND_ACTION,
      },
      {
        id: "phase-completion",
        label: "Finition",
        icon: "heroCheckCircle",
        description: "Chantiers en phase de finition",
        action: NOOP_COMMAND_ACTION,
      },
      {
        id: "phase-closed",
        label: "Clôturés",
        icon: "heroLockClosed",
        description: "Chantiers terminés",
        action: NOOP_COMMAND_ACTION,
      },
    ],
  },
  {
    id: "tasks",
    label: "Tâches",
    icon: "evaCheckSquare",
    items: [
      {
        id: "task-my-tasks",
        label: "Mes tâches",
        icon: "heroUser",
        shortcut: "G T",
        action: NOOP_COMMAND_ACTION,
      },
      {
        id: "task-urgent",
        label: "Tâches urgentes",
        icon: "heroExclamationTriangle",
        description: "Tâches à traiter en priorité",
        action: NOOP_COMMAND_ACTION,
      },
      {
        id: "task-today",
        label: "Tâches du jour",
        icon: "heroSun",
        action: NOOP_COMMAND_ACTION,
      },
      {
        id: "task-new",
        label: "Nouvelle tâche",
        icon: "heroPlus",
        shortcut: "N T",
        action: NOOP_COMMAND_ACTION,
      },
    ],
  },
];

/**
 * Configuration des sections par défaut pour le preset "admin"
 * Sections: Récents, Administration, Rapports, Exports
 */
export const PRESET_ADMIN_SECTIONS: CommandSection[] = [
  {
    id: "admin",
    label: "Administration",
    icon: "heroCog6Tooth",
    items: [
      {
        id: "admin-users",
        label: "Gestion utilisateurs",
        icon: "heroUsers",
        shortcut: "A U",
        action: NOOP_COMMAND_ACTION,
      },
      {
        id: "admin-company",
        label: "Paramètres entreprise",
        icon: "heroBuildingOffice",
        shortcut: "A E",
        action: NOOP_COMMAND_ACTION,
      },
      {
        id: "admin-permissions",
        label: "Permissions",
        icon: "heroShieldCheck",
        description: "Gérer les rôles et permissions",
        action: NOOP_COMMAND_ACTION,
      },
      {
        id: "admin-catalog",
        label: "Catalogue articles",
        icon: "heroCube",
        action: NOOP_COMMAND_ACTION,
      },
      {
        id: "admin-templates",
        label: "Modèles de documents",
        icon: "heroDocument",
        action: NOOP_COMMAND_ACTION,
      },
    ],
  },
  {
    id: "reports",
    label: "Rapports",
    icon: "heroChartBar",
    items: [
      {
        id: "report-activity",
        label: "Rapport d'activité",
        icon: "heroPresentationChartLine",
        shortcut: "R A",
        action: NOOP_COMMAND_ACTION,
      },
      {
        id: "report-financial",
        label: "Rapport financier",
        icon: "heroCurrencyDollar",
        shortcut: "R F",
        action: NOOP_COMMAND_ACTION,
      },
      {
        id: "report-projects",
        label: "Rapport chantiers",
        icon: "heroBuildingOffice",
        action: NOOP_COMMAND_ACTION,
      },
      {
        id: "report-employees",
        label: "Rapport employés",
        icon: "heroUsers",
        action: NOOP_COMMAND_ACTION,
      },
    ],
  },
  {
    id: "exports",
    label: "Exports",
    icon: "heroArrowDownTray",
    items: [
      {
        id: "export-clients",
        label: "Exporter clients",
        icon: "evaFileExcel",
        description: "Export CSV/Excel",
        action: NOOP_COMMAND_ACTION,
      },
      {
        id: "export-invoices",
        label: "Exporter factures",
        icon: "evaFileExcel",
        description: "Export CSV/Excel",
        action: NOOP_COMMAND_ACTION,
      },
      {
        id: "export-projects",
        label: "Exporter chantiers",
        icon: "evaFileExcel",
        description: "Export CSV/Excel",
        action: NOOP_COMMAND_ACTION,
      },
      {
        id: "export-backup",
        label: "Sauvegarde complète",
        icon: "heroCircleStack",
        description: "Télécharger toutes les données",
        action: NOOP_COMMAND_ACTION,
      },
    ],
  },
];

/**
 * Mapping des presets vers leurs configurations de sections
 */
export const PRESET_SECTIONS_MAP: Record<CommandPalettePreset, CommandSection[]> = {
  standard: PRESET_STANDARD_SECTIONS,
  project: PRESET_PROJECT_SECTIONS,
  admin: PRESET_ADMIN_SECTIONS,
};

/**
 * EVA Command Palette - Composant 100% custom
 *
 * Palette de commandes inspirée de VS Code, Notion, Linear.
 * Ouverture via Cmd+K / Ctrl+K avec navigation clavier complète.
 *
 * @example
 * // Simple
 * <eva-command-palette
 *   [(visible)]="showPalette"
 *   [sections]="commandSections"
 *   placeholder="Rechercher une action..."
 * ></eva-command-palette>
 *
 * // Avec callback sur sélection
 * <eva-command-palette
 *   [(visible)]="showPalette"
 *   [sections]="sections"
 *   (onSelect)="handleSelect($event)"
 * ></eva-command-palette>
 */
@Component({
  selector: "eva-command-palette",
  templateUrl: "./eva-command-palette.component.html",
  styleUrls: ["./eva-command-palette.component.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [CommonModule, NgIconComponent],
})
export class EvaCommandPaletteComponent implements OnInit, OnChanges {
  protected readonly isNgIconName = isNgIconName;

  // ==========================================================================
  // Inject
  // ==========================================================================

  private destroyRef = inject(DestroyRef);
  private recentsService = inject(EvaCommandPaletteRecentsService);

  // ==========================================================================
  // ViewChild
  // ==========================================================================

  @ViewChild("searchInput") searchInput!: ElementRef<HTMLInputElement>;

  // ==========================================================================
  // Inputs
  // ==========================================================================

  /** Visibilité de la palette */
  @Input() visible: boolean = false;

  /**
   * Preset de configuration.
   * - 'standard': Navigation + Actions (Récents, Navigation, Actions)
   * - 'project': Chantiers (Récents, Mes chantiers, Phases, Tâches)
   * - 'admin': Administration (Récents, Administration, Rapports, Exports)
   *
   * Si un preset est défini, il charge les sections par défaut.
   * Les sections personnalisées via @Input() sections sont ajoutées après.
   */
  @Input() preset: CommandPalettePreset | null = null;

  /** Activer la section Récents */
  @Input() enableRecents: boolean = true;

  /** Label de la section Récents */
  @Input() recentsLabel: string = "Récents";

  /**
   * Sections de commandes personnalisées.
   * Si un preset est défini, ces sections sont ajoutées APRÈS les sections du preset.
   * Sinon, ces sections sont utilisées directement.
   */
  @Input() sections: CommandSection[] = [];

  /** Placeholder du champ de recherche */
  @Input() placeholder: string = "Rechercher une commande...";

  /** Message quand aucun resultat */
  @Input() emptyMessage: string = "Aucun résultat trouvé";

  /** Fermer au clic sur le backdrop */
  @Input() dismissible: boolean = true;

  /** Largeur de la palette */
  @Input() width: string = "560px";

  /** Largeur maximale */
  @Input() maxWidth: string = "90vw";

  /** Afficher les raccourcis clavier */
  @Input() showShortcuts: boolean = true;

  /** Afficher les icones */
  @Input() showIcons: boolean = true;

  /** Bloquer le scroll du body */
  @Input() blockScroll: boolean = true;

  /** Afficher le skeleton de chargement (recherche async en cours) */
  @Input() loading: boolean = false;

  // ==========================================================================
  // Outputs
  // ==========================================================================

  /** Émis quand la visibilité change — pour le two-way binding [(visible)] */
  @Output() visibleChange = new EventEmitter<boolean>();
  /** Émis quand la palette s'ouvre */
  @Output() onShow = new EventEmitter<void>();
  /** Émis quand la palette se ferme */
  @Output() onHide = new EventEmitter<void>();
  /** Émis quand une commande est sélectionnée */
  @Output() onSelect = new EventEmitter<CommandItem>();
  /** Émis à chaque frappe dans le champ de recherche */
  @Output() onSearch = new EventEmitter<string>();

  // ==========================================================================
  // State
  // ==========================================================================

  /** Valeur de recherche */
  searchValue: string = "";

  /** Index de l'item selectionne */
  selectedIndex: number = 0;

  /** Animation en cours */
  private isAnimating: boolean = false;

  /** Buffer pour les raccourcis clavier à 2 touches (ex: "N O") */
  private shortcutBuffer: string = "";
  private shortcutTimer: any = null;

  /** Liste des récents */
  recents: RecentItem[] = [];

  /** Sections effectives (preset + custom) */
  private effectiveSections: CommandSection[] = [];

  constructor(private cdr: ChangeDetectorRef) {
    this.destroyRef.onDestroy(() => unlockBodyScroll(this));
  }

  // ==========================================================================
  // Lifecycle - OnChanges
  // ==========================================================================

  ngOnChanges(changes: SimpleChanges): void {
    // Recalculer les sections effectives si preset ou sections changent
    if (changes["preset"] || changes["sections"]) {
      this.updateEffectiveSections();
    }

    // Focus sur l'input quand visible passe à true (binding externe)
    if (changes["visible"]?.currentValue === true && !changes["visible"]?.previousValue) {
      this.searchValue = "";
      this.selectedIndex = 0;
      setTimeout(() => {
        this.searchInput?.nativeElement?.focus();
      }, 50);
    }
  }

  /**
   * Met à jour les sections effectives en combinant preset + sections custom
   */
  private updateEffectiveSections(): void {
    // Si un preset est défini, charger ses sections par défaut
    if (this.preset && PRESET_SECTIONS_MAP[this.preset]) {
      // Deep clone pour éviter les mutations des constantes
      const presetSections = PRESET_SECTIONS_MAP[this.preset].map((section) => ({
        ...section,
        items: [...section.items],
      }));

      // Ajouter les sections personnalisées après les sections du preset
      this.effectiveSections = [...presetSections, ...this.sections];
    } else {
      // Pas de preset, utiliser uniquement les sections personnalisées
      this.effectiveSections = this.sections;
    }

    this.cdr.markForCheck();
  }

  /**
   * Retourne les sections à afficher (pour le template)
   */
  get displaySections(): CommandSection[] {
    return this.effectiveSections;
  }

  // ==========================================================================
  // Lifecycle
  // ==========================================================================

  ngOnInit(): void {
    // Initialiser les sections effectives
    this.updateEffectiveSections();

    // S'abonner aux changements de récents
    this.recentsService.recents$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((recents) => {
      this.recents = recents;
      this.cdr.markForCheck();
    });
  }

  // ==========================================================================
  // Keyboard Shortcuts (Global)
  // ==========================================================================

  @HostListener("document:keydown", ["$event"])
  onKeyDown(event: KeyboardEvent): void {
    // Cmd+K ou Ctrl+K pour ouvrir
    if ((event.metaKey || event.ctrlKey) && event.key === "k") {
      event.preventDefault();
      if (this.visible) {
        this.hide();
      } else {
        this.show();
      }
      return;
    }

    // Si la palette n'est pas visible, ignorer
    if (!this.visible) return;

    // Shortcut sequence handling (e.g., "N O", "G D") when search is empty
    // Skip if the search input is focused — let the user type their search
    const isSearchFocused = this.searchInput?.nativeElement === document.activeElement;
    if (
      !isSearchFocused &&
      !this.searchValue &&
      !event.metaKey &&
      !event.ctrlKey &&
      !event.altKey &&
      event.key.length === 1 &&
      /^[a-zA-Z]$/.test(event.key)
    ) {
      event.preventDefault();
      this.handleShortcutKey(event.key.toUpperCase());
      return;
    }

    switch (event.key) {
      case "Escape":
        event.preventDefault();
        this.shortcutBuffer = "";
        this.hide();
        break;

      case "ArrowDown":
        event.preventDefault();
        this.navigateDown();
        break;

      case "ArrowUp":
        event.preventDefault();
        this.navigateUp();
        break;

      case "Enter":
        event.preventDefault();
        this.selectCurrentItem();
        break;

      case "Backspace":
        // Reset shortcut buffer on backspace
        this.shortcutBuffer = "";
        break;
    }
  }

  /**
   * Handle shortcut key sequences (e.g., "N" then "O" → "N O")
   */
  private handleShortcutKey(key: string): void {
    clearTimeout(this.shortcutTimer);

    this.shortcutBuffer += (this.shortcutBuffer ? " " : "") + key;

    // Try to find a matching item
    const allItems = this.displaySections.flatMap((s) => s.items);
    const match = allItems.find((item) => item.shortcut?.toUpperCase() === this.shortcutBuffer);

    if (match) {
      this.shortcutBuffer = "";
      this.selectItem(match);
      return;
    }

    // Check if any item starts with the current buffer (partial match)
    const hasPartial = allItems.some((item) => item.shortcut?.toUpperCase().startsWith(this.shortcutBuffer));

    if (hasPartial) {
      // Wait for next key
      this.shortcutTimer = setTimeout(() => {
        this.shortcutBuffer = "";
      }, 800);
    } else {
      // No match at all, reset
      this.shortcutBuffer = "";
    }
  }

  // ==========================================================================
  // Methods - Visibility
  // ==========================================================================

  show(): void {
    if (this.visible || this.isAnimating) return;

    this.visible = true;
    this.searchValue = "";
    this.selectedIndex = 0;
    this.visibleChange.emit(true);
    this.onShow.emit();

    if (this.blockScroll) {
      lockBodyScroll(this);
    }

    // Focus sur le champ de recherche apres le rendu
    setTimeout(() => {
      this.searchInput?.nativeElement?.focus();
    }, 50);

    this.cdr.markForCheck();
  }

  hide(): void {
    if (!this.visible || this.isAnimating) return;

    this.isAnimating = true;
    this.visible = false;
    this.shortcutBuffer = "";
    clearTimeout(this.shortcutTimer);
    this.visibleChange.emit(false);
    this.onHide.emit();

    unlockBodyScroll(this);

    setTimeout(() => {
      this.isAnimating = false;
      this.searchValue = "";
      this.selectedIndex = 0;
      this.cdr.markForCheck();
    }, 200);

    this.cdr.markForCheck();
  }

  // ==========================================================================
  // Methods - Search
  // ==========================================================================

  onSearchChange(value: string): void {
    this.searchValue = value;
    this.selectedIndex = 0;
    this.onSearch.emit(value);
    this.cdr.markForCheck();
  }

  clearSearch(): void {
    this.searchValue = "";
    this.selectedIndex = 0;
    this.onSearch.emit("");
    this.searchInput?.nativeElement?.focus();
    this.cdr.markForCheck();
  }

  // ==========================================================================
  // Methods - Filtering
  // ==========================================================================

  /**
   * Section des récents transformée en CommandSection
   */
  get recentsSection(): CommandSection | null {
    if (!this.enableRecents || this.recents.length === 0) {
      return null;
    }

    // Si recherche active, filtrer les récents
    const searchLower = this.searchValue.toLowerCase().trim();
    const filteredRecents = searchLower
      ? this.recents.filter((r) => r.label.toLowerCase().includes(searchLower))
      : this.recents;

    if (filteredRecents.length === 0) {
      return null;
    }

    return {
      id: "__recents__",
      label: this.recentsLabel,
      icon: "heroClock",
      items: filteredRecents.map((recent) => ({
        id: recent.id,
        label: recent.label,
        icon: recent.icon,
        description: this.recentsService.getLabelForType(recent.type),
        action: () => this.handleRecentSelect(recent),
        data: { recent, isRecent: true },
      })),
    };
  }

  get filteredSections(): CommandSection[] {
    const searchLower = this.searchValue.toLowerCase().trim();

    // Utiliser les sections effectives (preset + custom)
    const sourceSections = this.effectiveSections;

    // Filtrer les sections normales
    let sections: CommandSection[];
    if (!searchLower) {
      sections = sourceSections;
    } else {
      sections = sourceSections
        .map((section) => ({
          ...section,
          items: section.items.filter(
            (item) =>
              item.label.toLowerCase().includes(searchLower) || item.description?.toLowerCase().includes(searchLower),
          ),
        }))
        .filter((section) => section.items.length > 0);
    }

    // Ajouter la section récents en premier si elle existe
    const recents = this.recentsSection;
    if (recents) {
      return [recents, ...sections];
    }

    return sections;
  }

  get flatItems(): CommandItem[] {
    return this.filteredSections.flatMap((section) => section.items);
  }

  get hasResults(): boolean {
    return this.flatItems.length > 0;
  }

  // ==========================================================================
  // Methods - Navigation
  // ==========================================================================

  navigateDown(): void {
    const items = this.flatItems;
    if (items.length === 0) return;

    // Trouver le prochain item non-desactive
    let nextIndex = this.selectedIndex;
    do {
      nextIndex = (nextIndex + 1) % items.length;
    } while (items[nextIndex]?.disabled && nextIndex !== this.selectedIndex);

    this.selectedIndex = nextIndex;
    this.scrollToSelectedItem();
    this.cdr.markForCheck();
  }

  navigateUp(): void {
    const items = this.flatItems;
    if (items.length === 0) return;

    // Trouver l'item precedent non-desactive
    let prevIndex = this.selectedIndex;
    do {
      prevIndex = prevIndex - 1 < 0 ? items.length - 1 : prevIndex - 1;
    } while (items[prevIndex]?.disabled && prevIndex !== this.selectedIndex);

    this.selectedIndex = prevIndex;
    this.scrollToSelectedItem();
    this.cdr.markForCheck();
  }

  selectCurrentItem(): void {
    const items = this.flatItems;
    if (items.length === 0) return;

    const item = items[this.selectedIndex];
    if (item && !item.disabled) {
      this.selectItem(item);
    }
  }

  selectItem(item: CommandItem): void {
    if (item.disabled) return;

    this.onSelect.emit(item);
    item.action();

    if (!item.data?.keepOpen) {
      this.hide();
    }
  }

  setSearch(value: string): void {
    this.searchValue = value;
    this.selectedIndex = 0;
    this.onSearch.emit(value);
    setTimeout(() => this.searchInput?.nativeElement?.focus(), 0);
    this.cdr.markForCheck();
  }

  /**
   * Gestion de la sélection d'un récent
   */
  private handleRecentSelect(recent: RecentItem): void {
    // Mettre à jour le timestamp du récent (le remet en première position)
    this.recentsService.addRecent(recent);

    // Émettre un événement spécifique si nécessaire
    // La navigation sera gérée par le parent via onSelect
  }

  // ==========================================================================
  // Methods - Recents API
  // ==========================================================================

  /**
   * Ajoute un élément à l'historique des récents.
   * Peut être appelé depuis le parent pour enregistrer une action.
   *
   * @param item Élément à ajouter
   */
  addToRecents(item: Omit<RecentItem, "timestamp" | "icon"> & { icon?: string }): void {
    this.recentsService.addRecent(item);
  }

  /**
   * Supprime un élément de l'historique des récents.
   *
   * @param id Identifiant de l'élément
   */
  removeFromRecents(id: string): void {
    this.recentsService.removeRecent(id);
  }

  /**
   * Vide l'historique des récents.
   */
  clearRecents(): void {
    this.recentsService.clearRecents();
  }

  /**
   * Vérifie si un item est un récent (pour affichage différencié).
   *
   * @param item Item à vérifier
   */
  isRecentItem(item: CommandItem): boolean {
    return item.data?.isRecent === true;
  }

  isItemSelected(item: CommandItem): boolean {
    const items = this.flatItems;
    const index = items.indexOf(item);
    return index === this.selectedIndex;
  }

  private scrollToSelectedItem(): void {
    setTimeout(() => {
      const selectedEl = document.querySelector(".eva-command-palette__item--selected");
      selectedEl?.scrollIntoView({ block: "nearest", behavior: "auto" });
    }, 0);
  }

  // ==========================================================================
  // Methods - Events
  // ==========================================================================

  onBackdropClick(): void {
    if (this.dismissible) {
      this.hide();
    }
  }

  onContentClick(event: Event): void {
    event.stopPropagation();
  }

  onItemMouseEnter(item: CommandItem): void {
    if (item.disabled) return;
    const items = this.flatItems;
    const index = items.indexOf(item);
    if (index !== -1) {
      this.selectedIndex = index;
      this.cdr.markForCheck();
    }
  }

  // ==========================================================================
  // Methods - Styles
  // ==========================================================================

  get paletteStyle(): { [key: string]: string } {
    return {
      width: this.width,
      maxWidth: this.maxWidth,
    };
  }

  // ==========================================================================
  // Methods - TrackBy
  // ==========================================================================

  trackBySection(index: number, section: CommandSection): string {
    return section.id;
  }

  trackByItem(index: number, item: CommandItem): string {
    return item.id;
  }
}
